/*
 * File: NoticeToParents.jsx
 * Project: lets-talk-web
 */

/**
 * Imports
 */

// Modules
import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

// Utilities
import { createStateLocale } from '../../../../utilities/locale';

// Components
import {
	Meta,
	ContentNavigation,
	LegalNavigation,
	Typography,
	LocaleLink
} from '../../../../components';

// Styles
import * as S from './NoticeToParents.styles';


/**
 * Component
 */

const NoticeToParents = ({ meta, locale }) => {

	// Get current locale from hook
	const clientLocale = useSelector((state) => state.locale.value);
	const stateLocale = createStateLocale(clientLocale, locale);

	// Handle component initialization
	useEffect(() => {}, []);

	// Render component
	return (
		<>
			{/* Meta */}
			<Meta meta={meta} locale={stateLocale} />

			{/* Component Content */}
			<ContentNavigation>
				<LegalNavigation>
					<S.Wrapper>

						{/* Title */}
						<Typography tag="h1" weight="bold">
							Notice to Parents Regarding Collection, Use, and Disclosure of Children&apos;s Personal Information
						</Typography>

						{/* Organization */}
						<Typography tag="p" weight="bold">
							Let&apos;s Talk is operated by Teen Health Research, Inc. d/b/a Let&apos;s Talk Families
						</Typography>

						{/* Last Updated */}
						<Typography tag="p" weight="light" fontStyle="italic">
							Last updated July 2026
						</Typography>

						{/* Introduction */}
						<Typography tag="p" weight="light">
							<Typography tag="span" weight="bold" fontStyle="italic">
								Let&apos;s Talk (the “Platform”) is operated by Teen Health.
							</Typography>
							{' '}
							Thank you for registering your account with Let&apos;s Talk. This notice explains our practices regarding the collection, use, and disclosure of personal information provided by children under the age of 13, and how you can provide consent. Let&apos;s Talk may collect personal information from children when a parent creates an account for them on the Let&apos;s Talk platform. This notice applies when we collect information from your child when they use our online services, such as a website or app (the “Platform”).
						</Typography>

						<Typography tag="p" weight="light">
							For more information about our privacy practices, please review our
							{' '}
							<LocaleLink to="/legal/privacy" target="_blank">
								Privacy Policy
							</LocaleLink>
							.
						</Typography>

						{/* Personal Information We Collect */}
						<br />
						<Typography tag="h3" weight="bold">
							Personal Information We Collect from Children
						</Typography>

						<Typography tag="p" weight="light">
							We may collect the following personal information from children:
						</Typography>

						<Typography weight="light">
							<ul>
								<li>
									Account and profile information, including name or nickname, age, gender, username, and password
								</li>
								<li>
									User-generated content, including responses to quiz-style questions and open-ended reflection activities, questions and comments, audio messages to parents, and activity ratings
								</li>
								<li>
									Persistent identifiers, including information held in cookies, an Internet Protocol (IP) address, a processor or device serial number, or a unique device identifier
								</li>
							</ul>
						</Typography>

						{/* How We Use Information */}
						<br />
						<Typography tag="h3" weight="bold">
							How We Use Personal Information Collected from Children
						</Typography>

						<Typography tag="p" weight="light">
							We use the personal information we collect for the following purposes:
						</Typography>

						<Typography weight="light">
							<ul>
								<li>
									To provide, maintain, and improve the Platform, including to enable participation in interactive features of the Platform
								</li>
								<li>
									To create, maintain, and service your account
								</li>
								<li>
									To communicate with you about your account, including to respond to customer service requests from you and to send notifications, newsletters, postcards, or messages you request
								</li>
								<li>
									To conduct internal research and development
								</li>
								<li>
									To analyze traffic and user activities on the Platform, identify popular areas or features, and optimize and personalize the Platform
								</li>
								<li>
									To debug the Platform to identify and repair errors
								</li>
								<li>
									To process or fulfill orders and transactions
								</li>
								<li>
									To protect the rights and property of Let&apos;s Talk and others and comply with our legal obligations, including to detect, investigate, and prevent fraud and other illegal activities, protect the security and integrity of the Platform, and to enforce our agreements
								</li>
							</ul>
						</Typography>

						{/* How We Disclose Information */}
						<br />
						<Typography tag="h3" weight="bold">
							How We Disclose Personal Information Collected from Children
						</Typography>

						<Typography tag="p" weight="light">
							Let&apos;s Talk may disclose personal information we collect for the following purposes:
						</Typography>

						<Typography weight="light">
							<ul>
								<li>
									To operate the Platform and administer our business, such as to our service providers who perform a business, professional, or support function for us
								</li>
								<li>
									In connection with a corporate transaction, such as a merger, acquisition, or sale of our assets
								</li>
								<li>
									To protect the rights and property of Let&apos;s Talk and others, to comply with our legal obligations, including to respond to legal process or law enforcement requests or comply with mandatory reporting requirements; to detect, investigate and prevent fraud and other illegal activities; to protect the security and integrity of the Platform; and to enforce our agreements
								</li>
							</ul>
						</Typography>

						<Typography tag="p" weight="light">
							Let&apos;s Talk does not disclose children&apos;s personal information for marketing or other non-integral purposes.
						</Typography>

						{/* Providing Your Consent */}
						<br />
						<Typography tag="h3" weight="bold">
							Providing Your Consent
						</Typography>

						<Typography tag="p" weight="light">
							Let&apos;s Talk will not collect, use, or disclose your child&apos;s personal information without your consent except in limited circumstances permitted by applicable law. For example, if your child provides us with a name and online contact information and we have not already obtained your consent, then we would retain and use the information provided by your child to contact you, deliver this notice, and request your consent. If you do not consent within a reasonable time, then we will delete the name and contact information your child provided for this purpose.
						</Typography>

						<Typography tag="p" weight="light">
							You may provide verifiable parental consent through the registration process by completing the required payment transaction or other approved consent mechanism made available by Let&apos;s Talk. By completing the parental consent process, you authorize Let&apos;s Talk to collect, use, and disclose your child&apos;s personal information as described in this notice and our
							{' '}
							<LocaleLink to="/legal/privacy" target="_blank">
								Privacy Policy
							</LocaleLink>
							.
						</Typography>

						<Typography tag="p" weight="light">
							Following your registration, you will receive a confirmation email documenting your consent and payment.
						</Typography>

						<Typography tag="p" weight="light">
							You may withdraw your consent by emailing us at
							{' '}
							<LocaleLink to="mailto:hello@lets-talk-families.com?Subject=Withdraw Parental Consent">
								hello@lets-talk-families.com
							</LocaleLink>
							.
						</Typography>

						<Typography tag="p" weight="light">
							If you have questions about this notice, or our privacy practices as they relate to personal information we collect about children, please email us at
							{' '}
							<LocaleLink to="mailto:hello@lets-talk-families.com?Subject=Notice to Parents">
								hello@lets-talk-families.com
							</LocaleLink>
							.
						</Typography>

					</S.Wrapper>
				</LegalNavigation>
			</ContentNavigation>
		</>
	);
};


/**
 * Configuration
 */

NoticeToParents.propTypes = {
	meta: PropTypes.shape(),
	locale: PropTypes.shape()
};

NoticeToParents.defaultProps = {
	meta: {},
	locale: {}
};


/**
 * Exports
 */

export default NoticeToParents;
